<template>
  <Page>
    <div class="flex flex-col gap-2 h-full min-h-0">
      <h1 class="text-base font-semibold text-text lg:sr-only">Maintenance Build</h1>
      <div v-if="isDetailView || currentAnalysisId" class="bg-background-card rounded-lg shadow-card p-2.5 border border-border shrink-0">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
            <nav aria-label="Breadcrumb" data-testid="build-breadcrumb" class="min-w-0">
              <ol class="flex items-center gap-1.5 min-w-0">
                <li v-if="!isDetailView" aria-current="page" class="shrink-0 text-xl font-bold text-text">
                  {{ activeCollectionLabel }}
                </li>
                <template v-else>
                  <li class="shrink-0">
                    <button
                      type="button"
                      class="text-xl font-bold text-primary hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      @click="backToCollection"
                    >
                      {{ activeCollectionLabel }}
                    </button>
                  </li>
                  <template v-if="activeRecordLabel">
                    <li aria-hidden="true" data-testid="breadcrumb-separator" class="shrink-0 px-0.5 text-sm text-secondary"><i class="fa-solid fa-chevron-right"></i></li>
                    <li
                      aria-current="page"
                      :title="activeRecordLabel"
                      class="min-w-0 truncate font-mono text-xl font-semibold text-text"
                    >
                      {{ activeRecordLabel }}
                    </li>
                  </template>
                </template>
              </ol>
            </nav>
            <span
              v-if="isDetailView"
              class="text-xs rounded-full px-2.5 py-1 border shrink-0"
              :class="activeRecordStatusPill.class"
              data-testid="editor-status-pill"
            >
              {{ activeRecordStatusPill.label }}
            </span>
            <span
              v-if="store.planningAnalysisSummary"
              class="truncate text-xs bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 rounded-full px-2.5 py-1 border border-border shrink-0"
              data-testid="analysis-number"
            >
              {{ store.planningAnalysisSummary.analysisNumber }}
            </span>
            <Tag
              v-if="store.planningAnalysisSummary && methodologyLabel"
              :value="methodologyLabel"
              :class="methodologyBadgeClass(methodologyLabel)"
              data-testid="methodology-tag"
              class="shrink-0"
            />
            <Tag
              v-if="store.planningAnalysisSummary"
              :value="mplanStatusLabel[store.planningAnalysisSummary.mplanStatus]"
              :severity="mplanStatusSeverity[store.planningAnalysisSummary.mplanStatus]"
              data-testid="mplan-status"
              class="shrink-0"
            />
          </div>
          <Button
            v-if="currentAnalysisId"
            label="Review Assignments"
            icon="fa-solid fa-link"
            severity="secondary"
            outlined
            size="small"
            class="shrink-0"
            @click="navigateToAssignmentReview"
          />
        </div>
      </div>

      <BuildReadinessHeader
        class="shrink-0"
        :active-tab="activeTab"
        :navigation-visible="!isDetailView"
        :task-lists-count="store.taskLists.length"
        :items-count="store.maintenanceItems.length"
        :plans-count="store.plans.length"
        :coverage="coverageSummary"
        @navigate="handleTabChange"
        @open-assignments="navigateToAssignmentReview"
      />

      <Message v-if="store.error" severity="error" :closable="false" class="shrink-0">
        {{ store.error }}
      </Message>

      <div class="flex-1 min-h-0 flex flex-col">
        <!-- Deep-link resolution -->
        <div v-if="isResolvingDeepLink" class="flex flex-col items-center justify-center flex-1 text-secondary">
          <i class="fa-solid fa-spinner fa-spin text-2xl text-primary mb-3"></i>
          <p class="text-sm font-medium">Opening task list...</p>
        </div>

        <div v-else class="flex-1 min-h-0 flex flex-col">
          <!-- Task Lists workspace -->
          <template v-if="activeTab === 'taskLists'">
            <div v-if="store.selectedTaskListId" class="flex flex-col flex-1 min-h-0">
              <TaskListsTab class="flex-1 min-h-0" />
              <EditorFooter
                :is-saving="isSaving"
                :copy-disabled-reason="copyDisabledReason"
                @save="save"
                @copy="openCopyDialog"
              />
            </div>
            <TaskListLandingTab v-else class="flex-1 min-h-0" @edit="store.selectTaskList($event)" :copy-disabled-reason="copyDisabledReason" @copy="openCopyDialog" />
          </template>

          <!-- Items workspace -->
          <template v-else-if="activeTab === 'items'">
            <div v-if="store.selectedItemId" class="flex flex-col flex-1 min-h-0">
              <ItemDetailView class="flex-1 min-h-0" />
              <EditorFooter
                :is-saving="isSaving"
                :copy-disabled-reason="copyDisabledReason"
                @save="save"
                @copy="openCopyDialog"
              />
            </div>
            <ItemsLandingTab v-else class="flex-1 min-h-0" @edit="store.selectItem($event)" @create="handleCreateItem" :copy-disabled-reason="copyDisabledReason" @copy="openCopyDialog" />
          </template>

          <!-- Plans workspace -->
          <template v-else-if="activeTab === 'plans'">
            <div v-if="store.selectedPlanId" class="flex flex-col flex-1 min-h-0">
              <PlanDetailPanel class="flex-1 min-h-0" />
              <EditorFooter
                :is-saving="isSaving"
                :copy-disabled-reason="copyDisabledReason"
                @save="save"
                @copy="openCopyDialog"
              />
            </div>
            <PlansTab v-else class="flex-1 min-h-0" @edit="store.selectPlan($event)" :copy-disabled-reason="copyDisabledReason" @copy="openCopyDialog" />
          </template>
        </div>
      </div>
    </div>

    <CopyAssetDialog
      v-model:visible="showCopyDialog"
      :direction="copyDirection"
      :current-analysis-id="currentAnalysisId"
      @confirm="handleCopy"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Page from '@/shared/components/layouts/Page.vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import TaskListsTab from '../../components/planning/TaskListsTab.vue';
import TaskListLandingTab from '../../components/planning/TaskListLandingTab.vue';
import ItemsLandingTab from '../../components/planning/ItemsLandingTab.vue';
import ItemDetailView from '../../components/planning/ItemDetailView.vue';
import PlansTab from '../../components/planning/PlansTab.vue';
import PlanDetailPanel from '../../components/planning/PlanDetailPanel.vue';
import BuildReadinessHeader from '../../components/planning/BuildReadinessHeader.vue';
import EditorFooter from '../../components/planning/EditorFooter.vue';
import CopyAssetDialog from '../../components/dialogs/CopyAssetDialog.vue';
import { useMaintenanceStore, type PlanningTabId } from '../../stores/useMaintenanceStore';
import { useMaintenanceBuildPage } from '../../composables/useMaintenanceBuildPage';
import { mapAnalysisMethod } from '../../stores/maintenanceMappers';
import { methodologyBadgeClass } from '../../utils/methodologyLabels';
import { recordStatusPill } from '../../utils/recordStatusPill';
import type { MplanStatus } from '../../stores/maintenanceViewModels';
import { MaintenancePlansService } from '@/shared/services/maintenancePlans';
import { formatApiError } from '@/shared/utils/errorFormatters';
import { addToast } from '@/shared/utils/toastMessages';

definePage({ meta: { title: 'Maintenance Build' } });

const store = useMaintenanceStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const currentAnalysisId = typeof route.query.analysisId === 'string' ? route.query.analysisId : null;
const isSaving = ref(false);

const page = useMaintenanceBuildPage({ route, router, analysisId: currentAnalysisId });
const { activeTab, handleTabChange, isResolvingDeepLink } = page;

const collectionLabels: Record<PlanningTabId, string> = {
  taskLists: 'Task Lists',
  items: 'Items',
  plans: 'Plans',
};

const activeCollectionLabel = computed(() => collectionLabels[activeTab.value]);

const activeRecordLabel = computed(() => {
  if (activeTab.value === 'taskLists') return store.selectedTaskList?.taskListId ?? null;
  if (activeTab.value === 'items') return store.selectedMaintenanceItem?.itemId ?? null;
  return store.selectedPlan?.planId ?? null;
});

// True whenever the active collection has a record open, which is when the collection cards step
// aside and the breadcrumb carries the record identity.
const isDetailView = computed(() => {
  if (activeTab.value === 'taskLists') return Boolean(store.selectedTaskListId);
  if (activeTab.value === 'items') return Boolean(store.selectedItemId);
  return Boolean(store.selectedPlanId);
});

// The open record, so its save state can annotate its identity in the title island. The editor
// panes no longer repeat a status strip, and the footer stays actions-only.
const activeRecord = computed(() => {
  if (activeTab.value === 'taskLists') return store.selectedTaskList;
  if (activeTab.value === 'items') return store.selectedMaintenanceItem;
  return store.selectedPlan;
});

const activeRecordStatusPill = computed(() => recordStatusPill(activeRecord.value, store.isRecordDirty));

const methodologyLabel = computed(() => {
  const summary = store.planningAnalysisSummary;
  return summary ? mapAnalysisMethod(summary.method as Parameters<typeof mapAnalysisMethod>[0]) : null;
});

const mplanStatusLabel: Record<MplanStatus, string> = {
  NotStarted: 'Not started',
  PartiallyCovered: 'Partially covered',
  Covered: 'Covered',
};

const mplanStatusSeverity: Record<MplanStatus, string> = {
  NotStarted: 'secondary',
  PartiallyCovered: 'warn',
  Covered: 'success',
};

const coverageSummary = computed(() => {
  const summary = store.planningAnalysisSummary;
  if (!summary) return null;
  return {
    covered: summary.coveredRecommendationCount,
    assignable: summary.assignableRecommendationCount,
    total: summary.recommendationCount,
    uncovered: Math.max(0, summary.assignableRecommendationCount - summary.coveredRecommendationCount),
    excluded: summary.excludedRecommendationCount,
  };
});

// Copy is analysis-to-analysis; the unscoped Build view has no source/target analysis to copy with.
const copyDisabledReason = currentAnalysisId
  ? undefined
  : 'Open Maintenance Build from an analysis to copy its plans to or from another asset.';

const showCopyDialog = ref(false);
const copyDirection = ref<'from' | 'to'>('from');

function openCopyDialog(direction: 'from' | 'to') {
  copyDirection.value = direction;
  showCopyDialog.value = true;
}

function navigateToAssignmentReview(): void {
  if (!currentAnalysisId) return;
  router.push({ path: '/maintenance/assign', query: { analysisId: currentAnalysisId } });
}

function backToCollection(): void {
  if (activeTab.value === 'taskLists') {
    store.clearTaskListSelection();
  } else if (activeTab.value === 'items') {
    store.clearItemSelection();
  } else {
    store.clearPlanSelection();
  }
}

function handleCreateItem(): void {
  if (!store.canCreateMaintenanceItems) {
    return;
  }

  const newItem = store.createNewMaintenanceItem();
  store.selectItem(newItem.id);
}

onMounted(() => {
  void page.initialize().catch(() => {
    /* store.error already populated by the store */
  });
});

async function handleCopy(selectedAnalysisId: string): Promise<void> {
  if (!currentAnalysisId || !selectedAnalysisId) {
    return;
  }

  const sourceAnalysisId = copyDirection.value === 'from' ? selectedAnalysisId : currentAnalysisId;
  const targetAnalysisId = copyDirection.value === 'from' ? currentAnalysisId : selectedAnalysisId;
  const selectedAsset = store.maintainableAssets.find((asset) => (asset.analysisId ?? asset.id) === selectedAnalysisId);

  await MaintenancePlansService.copy({
    sourceAnalysisId,
    targetAnalysisId,
    objectIds: selectedAsset?.assetId ? [selectedAsset.assetId] : [],
    includeTaskLists: true,
    includePlans: true,
    includeItems: true,
  });

  await Promise.all([
    store.initializePlanning(currentAnalysisId),
    store.loadMaintainableAssets(),
    store.loadAvailableObjects(),
  ]);
}

const savedLabels: Record<PlanningTabId, string> = {
  taskLists: 'Task list saved',
  items: 'Item saved',
  plans: 'Plan saved',
};

async function save() {
  isSaving.value = true;
  try {
    const saved = await store.saveCurrentPlanningSelection(activeTab.value);
    if (saved) {
      addToast(toast, {
        severity: 'success',
        summary: savedLabels[activeTab.value],
        life: 3000,
      });
    }
  } catch (error) {
    addToast(toast, {
      severity: 'error',
      summary: 'Unable to save maintenance build changes',
      detail: store.error ?? formatApiError(error),
    });
  } finally {
    isSaving.value = false;
  }
}
</script>
